import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  STATUS_COLOURS,
  ACTIVITY_LABELS,
  formatChainage,
  isServiceActivity,
  isTmpActivity,
  activityFilterLabel,
  segmentMatchesFilter,
  activityFilterOptions,
  clamp,
  mergeIntervals,
} from "./VisualProgressTracker";
import {
  assetRoute,
  chainageSubPath,
  chainageToDistance,
  pointAtDistance,
  project,
  unproject,
  boundsOf,
  boundsCentre,
  fitZoom,
  metresPerPixel,
  tilesForViewport,
  TILE_PX,
} from "./routeGeo";

/**
 * SatelliteProgressPlan — the route, in plan, on satellite imagery, with the
 * sections coloured by their progress.
 *
 * This replaces the old 2.5D / 3D presentation. On site the useful question is
 * never "what does the trench look like in isometric" — it is "how far have we
 * actually got, and where". So: a flat satellite plan, the surveyed route drawn
 * along it, and each recorded section painted in its status colour.
 *
 * The basemap is plain XYZ tiles, so there is no SDK to load and no API key to
 * configure. Point REACT_APP_SATELLITE_TILE_URL at another {z}/{x}/{y} imagery
 * service to change providers.
 *
 * Props
 * -----
 *   asset         — the progressAssets doc (routeCoords or lat/lng anchors)
 *   assetTypeMeta — ASSET_TYPES[asset.assetType]
 *   segments      — progressSegments docs for this asset
 *   onClose       — close handler
 */

const DEFAULT_TILE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const LABEL_TILE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";

const TILE_URL = process.env.REACT_APP_SATELLITE_TILE_URL || DEFAULT_TILE_URL;
const TILE_ATTRIBUTION =
  process.env.REACT_APP_SATELLITE_TILE_URL
    ? "Satellite imagery"
    : "Imagery © Esri, Maxar, Earthstar Geographics";

const tileUrl = (template, { x, y, z }) =>
  template.replace("{z}", z).replace("{x}", x).replace("{y}", y);

const MIN_ZOOM = 3;
const MAX_ZOOM = 20;

export default function SatelliteProgressPlan({ asset, assetTypeMeta, segments = [], onClose }) {
  const hostRef = useRef(null);
  const [size, setSize] = useState({ width: 900, height: 560 });
  const [view, setView] = useState(null); // { centre, zoom } — null until fitted
  const [activity, setActivity] = useState("all");
  const [showLabels, setShowLabels] = useState(true);
  const [showMarkers, setShowMarkers] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);

  const assetStart = Number(asset?.startChainage ?? 0);
  const assetEnd = Number(asset?.endChainage ?? 0);
  const assetLength = Math.max(assetEnd - assetStart, 1);

  const route = useMemo(() => assetRoute(asset), [asset]);
  const isFootprint = (asset?.footprintCoords?.length || 0) >= 3;

  /* ----- Viewport measurement ----- */

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        setSize({ width: Math.round(r.width), height: Math.round(r.height) });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ----- Fit the route when the map first has a size ----- */

  const fitToRoute = useCallback(() => {
    if (!route) return;
    const bounds = boundsOf(route.coords);
    const zoom = clamp(fitZoom(bounds, size.width, size.height, { maxZoom: MAX_ZOOM, minZoom: MIN_ZOOM }), MIN_ZOOM, MAX_ZOOM);
    setView({ centre: boundsCentre(bounds), zoom });
  }, [route, size.width, size.height]);

  useEffect(() => {
    if (!view && route && size.width > 0) fitToRoute();
  }, [view, route, size.width, fitToRoute]);

  /* ----- ESC to close ----- */

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* ----- Segments to draw ----- */

  const visibleSegments = useMemo(
    () =>
      segments
        .filter((s) => segmentMatchesFilter(s, activity))
        // Crossings are a point, not a run — they draw as a marker instead.
        .filter((s) => Number.isFinite(Number(s.chainageStart))),
    [segments, activity]
  );

  /* Overall completion for the selected layer, so the header reads true
     regardless of which layer is on screen. */
  const completion = useMemo(() => {
    const done = visibleSegments
      .filter((s) => s.status === "completed")
      .map((s) => ({
        start: clamp(Number(s.chainageStart), assetStart, assetEnd),
        end: clamp(Number(s.chainageEnd), assetStart, assetEnd),
      }));
    const metres = mergeIntervals(done).reduce((sum, i) => sum + (i.end - i.start), 0);
    return { metres: Math.round(metres), pct: clamp((metres / assetLength) * 100, 0, 100) };
  }, [visibleSegments, assetStart, assetEnd, assetLength]);

  const activityOptions = useMemo(
    () => activityFilterOptions(assetTypeMeta?.activities || []),
    [assetTypeMeta]
  );

  /* ----- Projection helpers bound to the current view ----- */

  const origin = useMemo(() => {
    if (!view) return null;
    const c = project(view.centre.lat, view.centre.lng, view.zoom);
    return { x: c.x - size.width / 2, y: c.y - size.height / 2 };
  }, [view, size.width, size.height]);

  const toScreen = useCallback(
    (coord) => {
      if (!origin || !view) return { x: 0, y: 0 };
      const p = project(coord.lat, coord.lng, view.zoom);
      return { x: p.x - origin.x, y: p.y - origin.y };
    },
    [origin, view]
  );

  const pathFor = useCallback(
    (coords) => {
      if (!coords?.length) return "";
      return coords
        .map((c, i) => {
          const p = toScreen(c);
          return `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
        })
        .join(" ");
    },
    [toScreen]
  );

  const tiles = useMemo(
    () => (origin && view ? tilesForViewport({ originX: origin.x, originY: origin.y, width: size.width, height: size.height, zoom: view.zoom }) : []),
    [origin, view, size.width, size.height]
  );

  /* ----- Pan ----- */

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    dragRef.current = { x: e.clientX, y: e.clientY, moved: false };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPointer({ x: e.clientX - rect.left, y: e.clientY - rect.top });

    const drag = dragRef.current;
    if (!drag || !view || !origin) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    if (Math.abs(dx) + Math.abs(dy) < 1) return;
    dragRef.current = { x: e.clientX, y: e.clientY, moved: true };

    const nextCentre = unproject(
      origin.x + size.width / 2 - dx,
      origin.y + size.height / 2 - dy,
      view.zoom
    );
    setView((v) => ({ ...v, centre: nextCentre }));
  };

  const endDrag = (e) => {
    dragRef.current = null;
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  /* ----- Zoom (wheel + buttons), anchored on the cursor ----- */

  const zoomBy = useCallback(
    (delta, anchor) => {
      setView((v) => {
        if (!v) return v;
        const nextZoom = clamp(Math.round((v.zoom + delta) * 2) / 2, MIN_ZOOM, MAX_ZOOM);
        if (nextZoom === v.zoom) return v;
        if (!anchor) return { ...v, zoom: nextZoom };

        // Keep the geography under the cursor pinned while the scale changes.
        const c = project(v.centre.lat, v.centre.lng, v.zoom);
        const ox = c.x - size.width / 2;
        const oy = c.y - size.height / 2;
        const target = unproject(ox + anchor.x, oy + anchor.y, v.zoom);

        const t2 = project(target.lat, target.lng, nextZoom);
        const nextCentre = unproject(
          t2.x - anchor.x + size.width / 2,
          t2.y - anchor.y + size.height / 2,
          nextZoom
        );
        return { centre: nextCentre, zoom: nextZoom };
      });
    },
    [size.width, size.height]
  );

  const onWheel = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    zoomBy(e.deltaY < 0 ? 0.5 : -0.5, { x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  /* ----- Chainage markers every ~500 m ----- */

  const markers = useMemo(() => {
    if (!route) return [];
    const total = route.cum[route.cum.length - 1] || 0;
    const step = assetLength > 6000 ? 1000 : assetLength > 2000 ? 500 : 200;
    const out = [];
    for (let ch = Math.ceil(assetStart / step) * step; ch <= assetEnd; ch += step) {
      const d = chainageToDistance(ch, { assetStart, assetEnd, totalRouteLength: total });
      const p = pointAtDistance(route.coords, route.cum, d);
      if (p) out.push({ ch, coord: p });
    }
    return out;
  }, [route, assetStart, assetEnd, assetLength]);

  /* ----- Scale bar ----- */

  const scaleBar = useMemo(() => {
    if (!view) return null;
    const mpp = metresPerPixel(view.centre.lat, view.zoom);
    const targets = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
    const target = targets.find((t) => t / mpp > 60) || 5000;
    return { metres: target, px: Math.round(target / mpp) };
  }, [view]);

  const googleEarthUrl = useMemo(() => {
    const c = view?.centre || (route ? boundsCentre(boundsOf(route.coords)) : null);
    if (!c) return null;
    return `https://earth.google.com/web/@${c.lat},${c.lng},0a,2000d,35y,0h,0t,0r`;
  }, [view, route]);

  /* ----- Render ----- */

  const hasGeography = !!route;

  return createPortal(
    <div style={st.backdrop} onClick={onClose}>
      <div style={st.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={st.header}>
          <div style={st.headerLeft}>
            <span style={st.headerIcon}>{assetTypeMeta?.icon || "🛢️"}</span>
            <div>
              <h2 style={st.title}>{asset?.assetName}</h2>
              <span style={st.subtitle}>
                Satellite progress plan · {formatChainage(assetStart)} → {formatChainage(assetEnd)} ·{" "}
                {assetLength.toLocaleString("en-ZA")} m
              </span>
            </div>
          </div>

          <div style={st.headerRight}>
            <div style={st.stat}>
              <span style={st.statLabel}>
                {activity === "all" ? "Completed (all layers)" : `${activityFilterLabel(activity)} complete`}
              </span>
              <strong style={st.statValue}>{completion.pct.toFixed(1)}%</strong>
              <span style={st.statSub}>{completion.metres.toLocaleString("en-ZA")} m</span>
            </div>
            <button style={st.closeButton} onClick={onClose} title="Close (ESC)">×</button>
          </div>
        </div>

        {/* Controls */}
        <div style={st.controls}>
          <label style={st.control}>
            <span style={st.controlLabel}>Layer</span>
            <select style={st.select} value={activity} onChange={(e) => setActivity(e.target.value)}>
              <option value="all">All activities</option>
              {activityOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          <label style={st.toggle}>
            <input type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} />
            Place names
          </label>
          <label style={st.toggle}>
            <input type="checkbox" checked={showMarkers} onChange={(e) => setShowMarkers(e.target.checked)} />
            Chainage marks
          </label>

          <div style={st.controlSpacer} />

          <button style={st.ghostButton} onClick={fitToRoute} disabled={!hasGeography}>⤢ Fit route</button>
          {googleEarthUrl && (
            <a style={st.ghostButton} href={googleEarthUrl} target="_blank" rel="noopener noreferrer">
              🌍 Open in Google Earth
            </a>
          )}
        </div>

        {/* Map */}
        <div style={st.mapOuter}>
          <div
            ref={hostRef}
            style={{ ...st.map, cursor: dragging ? "grabbing" : "grab" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerLeave={() => { dragRef.current = null; setDragging(false); setHovered(null); }}
            onWheel={onWheel}
          >
            {!hasGeography ? (
              <div style={st.noGeo}>
                <span style={{ fontSize: 34 }}>🗺️</span>
                <p style={st.noGeoTitle}>This asset has no route yet</p>
                <p style={st.noGeoBody}>
                  Import a KMZ/KML route, or set the start and end GPS coordinates in Asset
                  Setup, and the plan will draw the route here with the recorded sections
                  coloured by status.
                </p>
              </div>
            ) : (
              <>
                {/* Imagery tiles */}
                {tiles.map((t) => (
                  <img
                    key={`sat-${t.key}`}
                    src={tileUrl(TILE_URL, t)}
                    alt=""
                    draggable={false}
                    style={{ ...st.tile, left: t.left, top: t.top }}
                  />
                ))}

                {/* Place-name reference tiles over the imagery */}
                {showLabels &&
                  tiles.map((t) => (
                    <img
                      key={`lbl-${t.key}`}
                      src={tileUrl(LABEL_TILE_URL, t)}
                      alt=""
                      draggable={false}
                      style={{ ...st.tile, left: t.left, top: t.top, opacity: 0.85 }}
                    />
                  ))}

                {/* Route + progress overlay */}
                <svg
                  width={size.width}
                  height={size.height}
                  style={st.overlay}
                  viewBox={`0 0 ${size.width} ${size.height}`}
                >
                  <defs>
                    <filter id="spCasing" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="1" stdDeviation="1.6" floodColor="#0f172a" floodOpacity="0.8" />
                    </filter>
                  </defs>

                  {/* A building footprint reads as an outline, not a line —
                      fill it so the plan shows the building, not a loop. */}
                  {isFootprint && (
                    <path
                      d={`${pathFor(route.coords)} Z`}
                      fill="rgba(245,158,11,0.28)"
                      stroke="#f59e0b"
                      strokeWidth="2.5"
                      strokeLinejoin="round"
                    />
                  )}

                  {/* The full route, as the "not started" baseline */}
                  <path d={pathFor(route.coords)} fill="none" stroke="#0f172a" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
                  <path d={pathFor(route.coords)} fill="none" stroke={STATUS_COLOURS.not_started} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />

                  {/* Each recorded section, painted over the baseline */}
                  {visibleSegments.map((seg) => {
                    const isPoint =
                      seg.activity === "services_crossing" ||
                      !Number.isFinite(Number(seg.chainageEnd)) ||
                      Number(seg.chainageEnd) <= Number(seg.chainageStart);
                    if (isPoint) return null;

                    const coords = chainageSubPath(route.coords, route.cum, {
                      assetStart,
                      assetEnd,
                      chainageStart: seg.chainageStart,
                      chainageEnd: seg.chainageEnd,
                    });
                    if (coords.length < 2) return null;

                    const colour = isTmpActivity(seg.activity)
                      ? "#f97316"
                      : isServiceActivity(seg.activity)
                      ? "#7c3aed"
                      : STATUS_COLOURS[seg.status] || STATUS_COLOURS.not_started;
                    const isHovered = hovered?.id === seg.id;

                    return (
                      <g key={seg.id}>
                        <path
                          d={pathFor(coords)}
                          fill="none"
                          stroke={colour}
                          strokeWidth={isHovered ? 11 : 7}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          opacity={isHovered ? 1 : 0.92}
                          filter="url(#spCasing)"
                        />
                        {/* Wet excavation reads as a dashed overlay so the two
                            material conditions stay apart on the plan. */}
                        {seg.excavationCondition === "wet" && (
                          <path
                            d={pathFor(coords)}
                            fill="none"
                            stroke="#e0f2fe"
                            strokeWidth="2.5"
                            strokeDasharray="6 5"
                            strokeLinecap="round"
                            pointerEvents="none"
                          />
                        )}
                        {/* Generous invisible hit line — a 7 px stroke is hard
                            to hover on a touchpad. */}
                        <path
                          d={pathFor(coords)}
                          fill="none"
                          stroke="transparent"
                          strokeWidth="18"
                          strokeLinecap="round"
                          style={{ cursor: "pointer" }}
                          onMouseEnter={() => setHovered(seg)}
                          onMouseLeave={() => setHovered(null)}
                        />
                      </g>
                    );
                  })}

                  {/* Crossing services — a point on the route */}
                  {visibleSegments
                    .filter((s) => s.activity === "services_crossing")
                    .map((seg) => {
                      const total = route.cum[route.cum.length - 1] || 0;
                      const p = pointAtDistance(
                        route.coords,
                        route.cum,
                        chainageToDistance(seg.chainageStart, { assetStart, assetEnd, totalRouteLength: total })
                      );
                      if (!p) return null;
                      const s = toScreen(p);
                      return (
                        <g key={seg.id} style={{ cursor: "pointer" }}
                          onMouseEnter={() => setHovered(seg)} onMouseLeave={() => setHovered(null)}>
                          <circle cx={s.x} cy={s.y} r="7" fill="#7c3aed" stroke="#ffffff" strokeWidth="2" />
                        </g>
                      );
                    })}

                  {/* Chainage marks */}
                  {showMarkers &&
                    markers.map((m) => {
                      const p = toScreen(m.coord);
                      if (p.x < -40 || p.x > size.width + 40 || p.y < -40 || p.y > size.height + 40) return null;
                      return (
                        <g key={m.ch} pointerEvents="none">
                          <circle cx={p.x} cy={p.y} r="3.5" fill="#ffffff" stroke="#0f172a" strokeWidth="1.4" />
                          <text
                            x={p.x + 7}
                            y={p.y - 6}
                            fontSize="11"
                            fontWeight="800"
                            fill="#ffffff"
                            stroke="#0f172a"
                            strokeWidth="3"
                            paintOrder="stroke"
                            fontFamily="ui-sans-serif, system-ui, sans-serif"
                          >
                            {formatChainage(m.ch)}
                          </text>
                        </g>
                      );
                    })}

                  {/* Route start / end — a closed footprint has neither */}
                  {(isFootprint ? [] : [
                    { coord: route.coords[0], label: "START", fill: "#22c55e" },
                    { coord: route.coords[route.coords.length - 1], label: "END", fill: "#ef4444" },
                  ]).map((end) => {
                    const p = toScreen(end.coord);
                    return (
                      <g key={end.label} pointerEvents="none">
                        <circle cx={p.x} cy={p.y} r="8" fill={end.fill} stroke="#ffffff" strokeWidth="2.5" />
                        <text
                          x={p.x} y={p.y - 14} textAnchor="middle" fontSize="11" fontWeight="900"
                          fill="#ffffff" stroke="#0f172a" strokeWidth="3.5" paintOrder="stroke"
                          fontFamily="ui-sans-serif, system-ui, sans-serif"
                        >
                          {end.label}
                        </text>
                      </g>
                    );
                  })}
                </svg>

                {/* Hover card */}
                {hovered && (
                  <div
                    style={{
                      ...st.hoverCard,
                      left: clamp(pointer.x + 16, 8, Math.max(size.width - 250, 8)),
                      top: clamp(pointer.y + 16, 8, Math.max(size.height - 120, 8)),
                    }}
                  >
                    <div style={st.hoverTitle}>
                      <span
                        style={{
                          ...st.hoverDot,
                          background: STATUS_COLOURS[hovered.status] || STATUS_COLOURS.not_started,
                        }}
                      />
                      {ACTIVITY_LABELS[hovered.activity] || hovered.activity}
                    </div>
                    <div style={st.hoverRow}>
                      {formatChainage(hovered.chainageStart)} → {formatChainage(hovered.chainageEnd)}
                    </div>
                    <div style={st.hoverMeta}>
                      {(hovered.status || "").replace(/_/g, " ")}
                      {hovered.excavationCondition ? ` · ${hovered.excavationCondition} material` : ""}
                      {hovered.diaryNo ? ` · diary #${hovered.diaryNo}` : ""}
                    </div>
                  </div>
                )}

                {/* Zoom buttons */}
                <div style={st.zoomBox}>
                  <button style={st.zoomBtn} onClick={() => zoomBy(1, { x: size.width / 2, y: size.height / 2 })}>+</button>
                  <button style={st.zoomBtn} onClick={() => zoomBy(-1, { x: size.width / 2, y: size.height / 2 })}>−</button>
                </div>

                {/* Scale bar + attribution */}
                {scaleBar && (
                  <div style={st.scaleBox}>
                    <div style={{ ...st.scaleBar, width: scaleBar.px }} />
                    <span style={st.scaleText}>
                      {scaleBar.metres >= 1000 ? `${scaleBar.metres / 1000} km` : `${scaleBar.metres} m`}
                    </span>
                  </div>
                )}
                <div style={st.attribution}>{TILE_ATTRIBUTION}</div>
              </>
            )}
          </div>
        </div>

        {/* Legend */}
        <div style={st.legend}>
          {[
            ["completed", "Completed"],
            ["in_progress", "In progress"],
            ["delayed", "Delayed"],
            ["rework", "Rework"],
            ["not_started", "Not started"],
          ].map(([key, label]) => (
            <span key={key} style={st.legendChip}>
              <span style={{ ...st.legendSwatch, background: STATUS_COLOURS[key] }} />
              {label}
            </span>
          ))}
          <span style={st.legendChip}>
            <span style={{ ...st.legendSwatch, background: "#f97316" }} />
            TMP
          </span>
          <span style={st.legendChip}>
            <span style={{ ...st.legendSwatch, background: "#7c3aed" }} />
            Existing services
          </span>
          <span style={st.legendHint}>
            {isFootprint ? "Building footprint from the imported KMZ · " : ""}
            Drag to pan · scroll to zoom · hover a section for its detail
            {route?.source === "anchors" ? " · straight line between GPS anchors (import a KMZ for the surveyed route)" : ""}
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const st = {
  backdrop: {
    position: "fixed", inset: 0, background: "rgba(2,6,23,0.85)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 18, zIndex: 2100,
  },
  modal: {
    background: "#0f172a", borderRadius: 20, width: "min(1400px, 100%)",
    height: "min(92vh, 960px)", display: "flex", flexDirection: "column",
    overflow: "hidden", boxShadow: "0 40px 100px rgba(0,0,0,0.6)",
    fontFamily: "ui-sans-serif, system-ui, sans-serif",
  },

  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 16, padding: "14px 20px", borderBottom: "1px solid #1e293b",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 13 },
  headerIcon: { fontSize: 26 },
  title: { margin: 0, fontSize: 18, fontWeight: 800, color: "#f8fafc" },
  subtitle: { fontSize: 12, color: "#94a3b8", fontWeight: 600 },
  headerRight: { display: "flex", alignItems: "center", gap: 14 },
  stat: { textAlign: "right", display: "grid", gap: 1 },
  statLabel: { fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "#64748b" },
  statValue: { fontSize: 22, fontWeight: 800, color: "#22c55e", lineHeight: 1.1 },
  statSub: { fontSize: 11, color: "#94a3b8", fontWeight: 700 },
  closeButton: {
    border: "1px solid #334155", background: "#1e293b", color: "#e2e8f0",
    width: 34, height: 34, borderRadius: 10, fontSize: 20, cursor: "pointer", lineHeight: 1,
  },

  controls: {
    display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
    padding: "10px 20px", borderBottom: "1px solid #1e293b", background: "#0b1220",
  },
  control: { display: "flex", alignItems: "center", gap: 8 },
  controlLabel: { fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "#64748b" },
  controlSpacer: { flex: 1 },
  select: {
    background: "#1e293b", color: "#e2e8f0", border: "1px solid #334155",
    borderRadius: 9, padding: "6px 10px", fontSize: 12, fontWeight: 700,
  },
  toggle: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#cbd5e1", cursor: "pointer" },
  ghostButton: {
    background: "#1e293b", color: "#e2e8f0", border: "1px solid #334155",
    borderRadius: 9, padding: "7px 13px", fontSize: 12, fontWeight: 700,
    cursor: "pointer", textDecoration: "none", display: "inline-block",
  },

  mapOuter: { flex: 1, minHeight: 0, padding: 0 },
  map: {
    position: "relative", width: "100%", height: "100%",
    overflow: "hidden", background: "#111827", touchAction: "none", userSelect: "none",
  },
  tile: {
    position: "absolute", width: TILE_PX, height: TILE_PX,
    pointerEvents: "none", userSelect: "none",
  },
  overlay: { position: "absolute", inset: 0 },

  noGeo: {
    position: "absolute", inset: 0, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: 6, padding: 40, textAlign: "center",
  },
  noGeoTitle: { margin: 0, fontSize: 16, fontWeight: 800, color: "#e2e8f0" },
  noGeoBody: { margin: 0, fontSize: 13, color: "#94a3b8", maxWidth: 460, lineHeight: 1.6 },

  hoverCard: {
    position: "absolute", background: "rgba(15,23,42,0.96)", color: "#f8fafc",
    border: "1px solid #334155", borderRadius: 10, padding: "9px 12px",
    fontSize: 12, display: "grid", gap: 3, minWidth: 190,
    pointerEvents: "none", boxShadow: "0 12px 30px rgba(0,0,0,0.5)",
  },
  hoverTitle: { display: "flex", alignItems: "center", gap: 7, fontWeight: 800 },
  hoverDot: { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 },
  hoverRow: { color: "#cbd5e1", fontWeight: 700, fontFamily: "ui-monospace, SFMono-Regular, monospace" },
  hoverMeta: { color: "#94a3b8", fontSize: 11, fontWeight: 600, textTransform: "capitalize" },

  zoomBox: { position: "absolute", right: 14, top: 14, display: "grid", gap: 4 },
  zoomBtn: {
    width: 32, height: 32, borderRadius: 8, border: "1px solid #334155",
    background: "rgba(15,23,42,0.9)", color: "#e2e8f0",
    fontSize: 18, fontWeight: 800, cursor: "pointer", lineHeight: 1,
  },

  scaleBox: { position: "absolute", left: 14, bottom: 14, display: "flex", alignItems: "center", gap: 8 },
  scaleBar: { height: 6, borderLeft: "2px solid #fff", borderRight: "2px solid #fff", borderBottom: "2px solid #fff" },
  scaleText: { fontSize: 11, fontWeight: 800, color: "#fff", textShadow: "0 1px 3px rgba(0,0,0,0.9)" },
  attribution: {
    position: "absolute", right: 10, bottom: 8, fontSize: 10, color: "rgba(255,255,255,0.75)",
    textShadow: "0 1px 3px rgba(0,0,0,0.9)", pointerEvents: "none",
  },

  legend: {
    display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
    padding: "10px 20px", borderTop: "1px solid #1e293b", background: "#0b1220",
  },
  legendChip: { display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 700, color: "#cbd5e1" },
  legendSwatch: { width: 18, height: 6, borderRadius: 3, display: "inline-block" },
  legendHint: { marginLeft: "auto", fontSize: 11, color: "#64748b", fontWeight: 600 },
};
